import { useMemo } from "react";
import { format } from "date-fns";

import { Badge } from "@/design-system/components/Badge/Badge";
import { Button } from "@/design-system/components/Button/Button";
import { ColumnConfig, Table } from "@/design-system/components/Table/Table";
import { Invite } from "@/components/users/types";
import FreshpaintUser from "@/components/users/FreshpaintUser";

import { InvitesListEmptyState } from "./TeamPageEmptyStates";

type InvitesListProps = {
  currentMember: FreshpaintUser;
  invites: Invite[];
  onClickDelete: (invite: Invite) => void;
};

export function InvitesList({
  currentMember,
  invites,
  onClickDelete,
}: InvitesListProps) {
  const columnConfigs = useMemo<ColumnConfig<Invite>[]>(() => {
    const configs: ColumnConfig<Invite>[] = [
      {
        label: "",
        CellContent: () => <Badge variant="info" text="Pending" />,
      },
      {
        label: "Email",
        CellContent: ({ row }) => (
          <span className="font-medium">{row.to_email}</span>
        ),
      },
      {
        label: "Invited",
        getCellText: (row) => format(new Date(row.created_at), "MMMM d, yyyy"),
      },
    ];

    if (currentMember.hasAdminAccess()) {
      configs.push({
        label: "",
        align: "right",
        CellContent: ({ row }) => (
          <Button variant="destructive" onClick={() => onClickDelete(row)}>
            Delete
          </Button>
        ),
      });
    }

    return configs;
  }, [currentMember, onClickDelete]);

  return (
    <div className="space-y-2">
      <h3 className="text-label-sm text-textSubtle font-medium">
        Pending invites
      </h3>
      <Table
        columnConfigs={columnConfigs}
        EmptyState={InvitesListEmptyState}
        getRowId={(row) => row.to_email}
        rows={invites}
      />
    </div>
  );
}
